import * as FS from 'node:fs';
import * as NodePath from 'node:path';
import { fileURLToPath } from 'node:url';

export type pathLike = string | URL | Pathway;

export { Pathway as t, Pathway };
class Pathway {
	readonly path: string;
	readonly #dirPath: string;
	readonly base: string;
	readonly ext: string;
	readonly name: string;
	readonly rawPath: string;

	constructor(path: string) {
		const normalized = NodePath.normalize(path);
		const parsed = NodePath.parse(normalized);

		this.path = normalized;
		this.#dirPath = parsed.dir;
		this.base = parsed.base;
		this.ext = parsed.ext;
		this.name = parsed.name;
		this.rawPath = path;
	}

	/**
	 * Joins additional path segments to this pathway, creating a new Pathway instance.
	 *
	 * @param paths - Path segments to join. Can be strings, URLs, or Pathway instances
	 * @returns A new Pathway instance with the joined path
	 *
	 * @example
	 * ```typescript
	 * const base = create('/home/user');
	 * const joined = base.join('documents', 'file.txt');
	 * console.log(joined.path); // '/home/user/documents/file.txt'
	 *
	 * // Works with different pathLike types
	 * const withUrl = base.join(new URL('file:///relative/path.js'));
	 * const withPathway = base.join(create('projects'), 'src');
	 * ```
	 */
	join(...paths: pathLike[]) {
		return new Pathway(NodePath.join(this.path, ...paths.map(pathToString)));
	}

	/**
	 * Joins additional path segments to this pathway and returns the resulting path as a string.
	 * This is equivalent to calling join().path but more convenient for getting string results.
	 *
	 * @param paths - Path segments to join. Can be strings, URLs, or Pathway instances
	 * @returns The joined path as a string
	 *
	 * @example
	 * ```typescript
	 * const base = create('/home/user');
	 * const resolved = base.resolve('documents', 'file.txt');
	 * console.log(resolved); // '/home/user/documents/file.txt'
	 *
	 * // Same as base.join('documents', 'file.txt').path
	 * ```
	 */
	resolve(...paths: pathLike[]) {
		return this.join(...paths).path;
	}

	/**
	 * Calculates the relative path from another path to this pathway.
	 *
	 * @param from - The base path to calculate relative from. Can be a string, URL, or Pathway instance
	 * @returns A new Pathway instance with the relative path
	 *
	 * @example
	 * ```typescript
	 * const target = create('/home/user/documents/file.txt');
	 *
	 * // From parent directory
	 * const fromParent = target.relativeFrom('/home/user');
	 * console.log(fromParent.path); // 'documents/file.txt'
	 *
	 * // From sibling directory
	 * const fromSibling = target.relativeFrom('/home/user/projects');
	 * console.log(fromSibling.path); // '../documents/file.txt'
	 *
	 * // From Pathway instance
	 * const base = create('/home/user/src');
	 * const relative = target.relativeFrom(base);
	 * console.log(relative.path); // '../documents/file.txt'
	 * ```
	 */
	relativeFrom(from: pathLike) {
		const path = NodePath.relative(pathToString(from), this.path);
		return new Pathway(path);
	}

	#dir?: Pathway;
	get dir() {
		this.#dir ??= new Pathway(this.#dirPath);
		return this.#dir;
	}

	/**
	 * Reads the contents of this directory and returns an array of Pathway instances.
	 *
	 * @param options - Optional configuration for directory reading
	 * @param options.recursive - Whether to read subdirectories recursively (default: false)
	 * @returns Array of Pathway instances representing the directory contents
	 *
	 * @example
	 * ```typescript
	 * const dir = create('/home/user/documents');
	 *
	 * // Read directory contents (non-recursive)
	 * const contents = dir.readDirSync();
	 * console.log(contents.map(p => p.base)); // ['file1.txt', 'file2.js', 'subdir']
	 *
	 * // Read directory contents recursively
	 * const allContents = dir.readDirSync({ recursive: true });
	 * console.log(allContents.map(p => p.path)); // All files and subdirectories
	 * ```
	 */
	readDirSync(options?: { recursive?: boolean }) {
		const paths = FS.readdirSync(this.path, {
			recursive: options?.recursive ?? false,
			encoding: 'utf-8',
		});

		return paths.map((path) => this.join(path));
	}

	/**
	 * Searches up the directory tree from this pathway to find a path that matches the test function.
	 *
	 * @param params - Configuration object for the search
	 * @param params.test - Function that tests if a path matches the search criteria
	 * @param params.skip - Optional function to skip certain paths during traversal
	 * @returns The matching Pathway instance, or undefined if not found
	 *
	 * @example
	 * ```typescript
	 * const currentFile = create('/home/user/project/src/file.js');
	 *
	 * // Find the nearest directory containing a package.json file
	 * const packageRoot = currentFile.findUp({
	 *   test: (path) => FS.existsSync(NodePath.join(path, 'package.json'))
	 * });
	 *
	 * if (packageRoot) {
	 *   console.log('Package root:', packageRoot.path);
	 * }
	 * ```
	 */
	findUp(params: findParams): Pathway | undefined {
		let path: string = this.path;

		while (true) {
			if (params.test(path)) {
				return create(path);
			}

			const oldDir = path;
			path = NodePath.dirname(path);

			if (path === oldDir) {
				break;
			}
		}

		return undefined;
	}

	/**
	 * Searches up the directory tree from this pathway to find a path that matches the test function.
	 * Throws an error if no matching path is found.
	 *
	 * @param params - Configuration object for the search
	 * @param params.test - Function that tests if a path matches the search criteria
	 * @param params.skip - Optional function to skip certain paths during traversal
	 * @returns The matching Pathway instance
	 * @throws {TargetPathNotFoundError} When no matching path is found
	 *
	 * @example
	 * ```typescript
	 * const currentFile = create('/home/user/project/src/file.js');
	 *
	 * // Find the nearest directory containing a package.json file, or throw an error
	 * const packageRoot = currentFile.findUpStrict({
	 *   test: (path) => FS.existsSync(NodePath.join(path, 'package.json'))
	 * });
	 *
	 * console.log('Package root:', packageRoot.path); // Will always execute or throw
	 * ```
	 */
	findUpStrict(params: findParams) {
		const path = this.findUp(params);
		if (path == null) {
			throw new TargetPathNotFoundError();
		}
		return path;
	}

	#packageDirTest(path: string) {
		return FS.existsSync(NodePath.join(path, 'package.json'));
	}

	/**
	 * Finds the nearest directory containing a package.json file by searching up from this pathway.
	 *
	 * @param params - Optional configuration to customize the search behavior
	 * @param params.test - Optional custom test function (defaults to checking for package.json)
	 * @param params.skip - Optional function to skip certain paths during traversal
	 * @returns The directory containing package.json as a Pathway instance, or undefined if not found
	 *
	 * @example
	 * ```typescript
	 * const currentFile = create('/home/user/project/src/file.js');
	 *
	 * // Find the package root directory
	 * const packageRoot = currentFile.packageDir();
	 * if (packageRoot) {
	 *   console.log('Package found at:', packageRoot.path);
	 * }
	 * ```
	 */
	packageDir(params?: Partial<findParams>) {
		return this.findUpStrict({
			test: (path) => this.#packageDirTest(path),
			...params,
		});
	}

	/**
	 * Finds the nearest directory containing a package.json file by searching up from this pathway.
	 * Throws an error if no package.json is found.
	 *
	 * @param params - Optional configuration to customize the search behavior
	 * @param params.test - Optional custom test function (defaults to checking for package.json)
	 * @param params.skip - Optional function to skip certain paths during traversal
	 * @returns The directory containing package.json as a Pathway instance
	 * @throws {TargetPathNotFoundError} When no package.json is found
	 *
	 * @example
	 * ```typescript
	 * const currentFile = create('/home/user/project/src/file.js');
	 *
	 * // Find the package root directory, or throw an error
	 * const packageRoot = currentFile.packageDirStrict();
	 * console.log('Package found at:', packageRoot.path);
	 * ```
	 */
	packageDirStrict(params?: Partial<findParams>) {
		return this.findUpStrict({
			test: (path) => this.#packageDirTest(path),
			...params,
		});
	}
}

/**
 * Creates a new Pathway instance from various path-like inputs.
 *
 * @param path - The path to create a Pathway from. Can be a string, URL, or existing Pathway instance
 * @returns A new Pathway instance with normalized path properties
 *
 * @example
 * ```typescript
 * // From string
 * const pathway1 = create('/home/user/file.txt');
 *
 * // From URL
 * const pathway2 = create(new URL('file:///home/user/file.txt'));
 *
 * // From file:// protocol string
 * const pathway3 = create('file:///home/user/file.txt');
 *
 * // From existing Pathway
 * const pathway4 = create(pathway1);
 * ```
 */
export const create = (path: pathLike) => new Pathway(pathToString(path));

export const pathToString = (pathLike: pathLike): string => {
	if (pathLike instanceof URL) {
		return fileURLToPath(pathLike);
	}

	if (pathLike instanceof Pathway) {
		return pathLike.path;
	}

	if (pathLike.startsWith('file://')) {
		return fileURLToPath(pathLike);
	}

	return pathLike;
};

export type findParams = {
	test: (path: string) => boolean;
	skip?: (path: string) => boolean;
};

export class TargetPathNotFoundError extends Error {
	constructor() {
		super('Target path not found');
	}
}
